import { memo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLayoutPreferences } from '../../../context/LayoutPreferencesContext';
import { AnimationInputData } from '../../../hooks/Marketing';

interface AnimationFormProps {
  currentAnimation: AnimationInputData | null;
  saving: boolean;
  saveError: string;
  onHasTitreChange: (v: boolean) => void;
  onValuesChange: (titre: string, desc: string) => void;
  onSave: () => void;
  onReset: () => void;
}

const AnimationForm = memo(function AnimationForm({
  currentAnimation, saving, saveError,
  onHasTitreChange, onValuesChange, onSave, onReset,
}: AnimationFormProps) {
  const { sidebarColor } = useLayoutPreferences();
  const [titre, setTitre] = useState('');
  const [description, setDescription] = useState('');

  const handleTitreChange = (v: string) => {
    setTitre(v);
    onHasTitreChange(v.trim() !== '');
    onValuesChange(v, description);
  };
  const handleDescChange = (v: string) => {
    setDescription(v);
    onValuesChange(titre, v);
  };

  return (
    <>
      {saveError !== '' && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          label="Titre *"
          size="small"
          value={titre}
          onChange={e => handleTitreChange(e.target.value)}
          disabled={currentAnimation !== null}
          sx={{ minWidth: 220 }}
        />
        <TextField
          label="Description"
          size="small"
          value={description}
          onChange={e => handleDescChange(e.target.value)}
          disabled={currentAnimation !== null}
          sx={{ flexGrow: 1, minWidth: 260 }}
        />
        {currentAnimation === null && (
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={onSave}
            disabled={saving || titre.trim() === ''}
            sx={{
              height: 40,
              backgroundColor: sidebarColor,
              '&:hover': { backgroundColor: sidebarColor, filter: 'brightness(0.9)' },
              whiteSpace: 'nowrap',
              textTransform: 'uppercase',
              fontWeight: 600,
              fontSize: '0.75rem',
              letterSpacing: 0.8,
            }}
          >
            Sauvegarder l&apos;animation
          </Button>
        )}
        {currentAnimation !== null && (
          <Button
            variant="text"
            size="small"
            onClick={onReset}
            sx={{ color: '#6c757d', textTransform: 'none', fontSize: '0.75rem' }}
          >
            Nouvelle animation
          </Button>
        )}
      </Box>
      {currentAnimation !== null && (
        <Alert severity="success" icon={false} sx={{ mt: 2, py: 0.5, fontSize: '0.8rem' }}>
          Animation <strong>{currentAnimation.title}</strong> créée
        </Alert>
      )}
      {currentAnimation === null && titre.trim() !== '' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.5 }}>
          <InfoOutlinedIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            L&apos;animation sera sauvegardée automatiquement lors de l&apos;ajout de la première communication.
          </Typography>
        </Box>
      )}
    </>
  );
});

export default AnimationForm;
